/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { signOut } from "firebase/auth";
import SearchModal from "./components/SearchModal";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import KingdomsPage from "./pages/KingdomsPage";
import PhylumPage from "./pages/PhylumPage";
import ClassPage from "./pages/ClassPage";
import OrderPage from "./pages/OrderPage";
import FamilyPage from "./pages/FamilyPage";
import GenusPage from "./pages/GenusPage";
import SpeciesPage from "./pages/SpeciesPage";
import UploadPage from "./pages/UploadPage";
import AdminAuditPage from "./pages/AdminAuditPage";
import AdminEmptyGeneraPage from "./pages/AdminEmptyGeneraPage";
import ProfilePage from "./pages/ProfilePage";
import AdminDefaultTaxaImagesPage from "./pages/AdminDefaultTaxaImagesPage";
import QuizSetupPage from "./pages/QuizSetupPage";
import QuizPage from "./pages/QuizPage";
import LoginPage from "./pages/LoginPage";
import { devNotes } from "./data/devNotes";
import { wishList } from "./data/wishList";
import devNotesIcon from "./assets/devnotes.png";
import wishListIcon from "./assets/wishlist.png";
import TestMap from "./components/TestMap";
import LocationMigrationPage from "./pages/LocationMigrationPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CopyrightPage from "./pages/CopyrightPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import ContactPage from "./pages/ContactPage";
import PolicyConsentPage from "./pages/PolicyConsentPage";
import AdminPage from "./pages/AdminPage";
import AdminTaxonomyPage from "./pages/AdminTaxonomyPage";
import TaxonomicNotices from "./components/TaxonomicNotices";
import TaxonomicReviewPage from "./pages/TaxonomicReviewPage";
import useAdminClaim from "./hooks/useAdminClaim";
import { hasAcceptedCurrentPolicies } from "./constants/policies";
import ForumPage from "./pages/ForumPage";
import ForumTopicPage from "./pages/ForumTopicPage";
import CommunityNavBadge from "./components/CommunityNavBadge";
import { NotificationBadgeProvider } from "./context/NotificationBadgeContext";

function Home() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const { isAdmin } = useAdminClaim();

  const [seenDevNotesCount, setSeenDevNotesCount] = useState(devNotes.length);
  const [seenWishListCount, setSeenWishListCount] = useState(wishList.length);

  const newDevNotesCount = Math.max(0, devNotes.length - seenDevNotesCount);
  const newWishListCount = Math.max(0, wishList.length - seenWishListCount);

  useEffect(() => {
    async function loadSeenAppUpdates() {
      if (!auth.currentUser) return;

      try {
        const snap = await getDoc(doc(db, "profile", auth.currentUser.uid));
        const seenAppUpdates = snap.exists()
          ? snap.data().seenAppUpdates || {}
          : {};

        setSeenDevNotesCount(Number(seenAppUpdates.devNotes ?? 0));
        setSeenWishListCount(Number(seenAppUpdates.wishList ?? 0));
      } catch (err) {
        console.error("Failed to load seen app updates:", err);
        setSeenDevNotesCount(0);
        setSeenWishListCount(0);
      }
    }

    loadSeenAppUpdates();
  }, []);

  async function openDevNotes() {
    setNotesOpen(true);
    setSeenDevNotesCount(devNotes.length);

    if (!auth.currentUser) return;

    try {
      await setDoc(
        doc(db, "profile", auth.currentUser.uid),
        {
          seenAppUpdates: {
            devNotes: devNotes.length,
          },
        },
        { merge: true },
      );
    } catch (err) {
      console.error("Failed to save seen devnotes count:", err);
    }
  }

  async function openWishList() {
    setWishlistOpen(true);
    setSeenWishListCount(wishList.length);

    if (!auth.currentUser) return;

    try {
      await setDoc(
        doc(db, "profile", auth.currentUser.uid),
        {
          seenAppUpdates: {
            wishList: wishList.length,
          },
        },
        { merge: true },
      );
    } catch (err) {
      console.error("Failed to save seen wishlist count:", err);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#374049] overflow-y-auto px-4 py-6 min-[400px]:overflow-hidden">
      {" "}
      <div className="relative w-full max-w-sm min-[400px]:h-auto px-6 min-[400px]:px-10 pt-16 min-[400px]:pt-16 pb-8 min-[400px]:pb-2 bg-white rounded-3xl shadow-lg border border-[#93CA87]/40 text-center flex flex-col justify-center overflow-hidden">
        {" "}
        <div className="-mx-6 min-[400px]:-mx-10 -mt-16 min-[400px]:-mt-16 mb-10 h-36 overflow-hidden rounded-t-3xl bg-[#374049] flex items-center justify-center relative">
          {" "}
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="w-full h-full object-cover object-center"
          />
          <button
            onClick={openDevNotes}
            className="absolute left-5 top-5 opacity-80 hover:opacity-100 transition"
            title="Developer notes"
          >
            <div className="relative w-12 h-12 flex items-center justify-center animate-breathe hover:animate-none">
              {newDevNotesCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 rounded-full bg-[#24c3be] text-white text-[10px] font-semibold flex items-center justify-center shadow-md border border-white">
                  +{newDevNotesCount}
                </span>
              )}
              <img
                src={devNotesIcon}
                alt="Developer notes"
                className="w-12 h-12 object-contain drop-shadow-md"
              />
            </div>
          </button>
          <button
            onClick={openWishList}
            className="absolute right-5 top-5 opacity-80 hover:opacity-100 transition"
            title="Wish List"
          >
            <div className="relative w-12 h-12 flex items-center justify-center animate-breathe hover:animate-none">
              {newWishListCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 rounded-full bg-[#24c3be] text-white text-[10px] font-semibold flex items-center justify-center shadow-md border border-white">
                  +{newWishListCount}
                </span>
              )}
              <img
                src={wishListIcon}
                alt="Wish List"
                className="w-12 h-12 object-contain drop-shadow-md"
              />
            </div>
          </button>
        </div>
        <div className="relative z-10 w-full max-w-[18rem] mx-auto text-center">
          {" "}
          <div className="flex flex-col gap-3 sm:gap-4 items-center">
            {" "}
            <Link
              to="/upload"
              className="py-2.5 rounded-xl w-full max-w-[16rem] bg-[#24C3BE] text-white font-medium tracking-wide hover:bg-[#1faeaa] transition text-center"
            >
              Add Photos
            </Link>
            <Link
              to="/explore"
              className="py-2.5 rounded-xl w-full max-w-[16rem] border border-[#93CA87] text-[#374049] text-center font-medium hover:bg-[#93CA87]/15 transition"
            >
              Explore Taxonomy
            </Link>
            <Link
              to="/profile"
              className="py-2.5 rounded-xl w-full max-w-[16rem] border border-[#93CA87] text-[#374049] text-center font-medium hover:bg-[#93CA87]/15 transition"
            >
              Profile
            </Link>
            {
              <Link
                to="/forum"
                className="relative py-2.5 rounded-xl w-full max-w-[16rem] border border-[#93CA87] text-[#374049] text-center font-medium hover:bg-[#93CA87]/15 transition"
              >
                Community
                <CommunityNavBadge />
              </Link>
            }
            {isAdmin && (
              <Link
                to="/admin"
                className="py-2.5 rounded-xl w-full max-w-[16rem] border border-[#374049] bg-[#374049] text-white text-center font-medium hover:bg-[#2f373e] transition"
              >
                Admin Mode
              </Link>
            )}
            <div className="w-full max-w-[16rem] flex flex-col items-start gap-0">
              {" "}
              <button
                onClick={async () => {
                  await signOut(auth);
                }}
                className="py-2.5 rounded-xl w-full border border-[#374049] text-[#374049] text-center font-medium hover:bg-[#374049]/10 transition"
              >
                Sign Out
              </button>
              <a
                href="https://ko-fi.com/X5I5209XCB"
                target="_blank"
                rel="noreferrer"
                className="mx-auto mt-7 flex items-center gap-3.5 px-3 py-1.5 rounded-xl bg-[#24C3BE] text-white shadow-md hover:bg-[#1faeaa] hover:shadow-lg transition"
                title="Support BioIndex on Ko-fi"
              >
                <img
                  src="https://storage.ko-fi.com/cdn/cup-border.png"
                  alt="Ko-fi"
                  className="w-5 h-5 object-contain"
                />
                <span className="text-xs font-semibold whitespace-nowrap">
                  Show your support!
                </span>
              </a>
            </div>
          </div>
        </div>
        <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        <footer className="relative z-10 mt-4 border-t border-[#374049]/10 pt-4 text-center">
          <p className="text-[11px] text-[#374049]/60">
            © {new Date().getFullYear()} BioIndex™. All rights reserved.
          </p>

          <div className="mt-1 flex justify-center gap-3 text-[10px] text-[#374049]/60">
            <Link to="/terms" className="hover:text-[#24C3BE]">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-[#24C3BE]">
              Privacy
            </Link>
            <Link to="/community-guidelines" className="hover:text-[#24C3BE]">
              Guidelines
            </Link>
            <Link to="/copyright" className="hover:text-[#24C3BE]">
              Copyright
            </Link>
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              Contact
            </Link>
          </div>
        </footer>
      </div>
      {/* Developer Notes Modal */}
      {notesOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white max-w-lg w-full rounded-2xl p-6 shadow-xl">
            <h2
              className="text-2xl mb-4 text-slate-800"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Developer Notes
            </h2>

            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {devNotes.map((note, i) => (
                <div key={i} className="border-b pb-3">
                  <div className="text-xs text-slate-400">{note.date}</div>

                  <div className="text-sm font-semibold text-slate-800 mt-1">
                    {note.title}
                  </div>

                  <div className="text-sm text-slate-600 mt-1">{note.text}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setNotesOpen(false)}
              className="mt-6 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Wish List Modal */}
      {wishlistOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white max-w-lg w-full rounded-2xl p-6 shadow-xl">
            <h2
              className="text-2xl mb-4 text-slate-800"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Wish List
            </h2>

            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {wishList.map((item, i) => (
                <div key={i} className="border-b pb-3">
                  <div className="text-xs text-slate-400">{item.date}</div>

                  <div className="text-sm font-semibold text-slate-800 mt-1">
                    {item.title}
                  </div>

                  <div className="text-sm text-slate-600 mt-1">{item.text}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setWishlistOpen(false)}
              className="mt-6 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-100"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminRoute({ children }) {
  const { adminLoading, isAdmin } = useAdminClaim();

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#374049] px-4 text-sm text-white/70">
        Verifying administrator access...
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ProfilePageRoute() {
  const { uid } = useParams();

  return <ProfilePage key={uid || "own-profile"} />;
}

function SuspendedAccountPage({ accountStatus }) {
  const suspendedDate = accountStatus?.suspendedAt?.toDate?.();

  const formattedDate = suspendedDate
    ? new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(suspendedDate)
    : "";

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#374049] px-4 py-8">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl">
        <header className="border-b border-red-100 bg-red-50 px-6 py-6 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red-600">
            Account Restricted
          </p>

          <h1
            className="mt-1 text-4xl text-[#374049]"
            style={{
              fontFamily: "Cormorant Garamond",
            }}
          >
            Account Suspended
          </h1>
        </header>

        <div className="p-6 text-center sm:p-8">
          <p className="text-sm leading-7 text-[#374049]/75">
            Your BioIndex account has been suspended. You cannot upload
            observations, edit content, participate in the forum, vote, send
            friend requests, or perform other account actions while the
            suspension remains active.
          </p>

          {accountStatus?.reason && (
            <div className="mt-5 rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-left">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/50">
                Reason
              </p>

              <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-[#374049]">
                {accountStatus.reason}
              </p>
            </div>
          )}

          {formattedDate && (
            <p className="mt-3 text-xs text-slate-400">
              Suspended {formattedDate}
            </p>
          )}

          <p className="mt-5 text-xs leading-6 text-slate-500">
            This page will update automatically when an administrator restores
            your account.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              Contact BioIndex
            </Link>

            <button
              type="button"
              onClick={() => signOut(auth)}
              className="rounded-xl border border-[#374049] bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-[#374049] hover:text-white"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

function AppRoutes({
  user,
  policyAcceptanceLoaded,
  hasAcceptedPolicies,
  accountSuspended,
  accountStatus,
}) {
  const location = useLocation();

  const publicPolicyPages = {
    "/terms": TermsPage,
    "/privacy": PrivacyPage,
    "/copyright": CopyrightPage,
    "/community-guidelines": CommunityGuidelinesPage,
    "/contact": ContactPage,
  };

  const PublicPolicyPage = publicPolicyPages[location.pathname];

  if (PublicPolicyPage) {
    return <PublicPolicyPage />;
  }

  if (user === undefined) {
    return <div style={{ padding: "40px" }}>Loading...</div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  if (!policyAcceptanceLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#374049] px-4">
        <p className="text-sm text-[#FCFFF6]">Loading your account...</p>
      </div>
    );
  }

  if (accountSuspended) {
    return <SuspendedAccountPage accountStatus={accountStatus} />;
  }

  if (!hasAcceptedPolicies) {
    return <PolicyConsentPage user={user} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/explore" element={<KingdomsPage />} />
      <Route path="/explore/:uid" element={<KingdomsPage />} />
      <Route path="/kingdom/:kingdomName" element={<PhylumPage />} />
      <Route
        path="/explore/:uid/kingdom/:kingdomName"
        element={<PhylumPage />}
      />
      <Route
        path="/kingdom/:kingdomName/phylum/:phylumName"
        element={<ClassPage />}
      />
      <Route
        path="/explore/:uid/kingdom/:kingdomName/phylum/:phylumName"
        element={<ClassPage />}
      />
      <Route
        path="/kingdom/:kingdomName/phylum/:phylumName/class/:className"
        element={<OrderPage />}
      />
      <Route
        path="/explore/:uid/kingdom/:kingdomName/phylum/:phylumName/class/:className"
        element={<OrderPage />}
      />
      <Route
        path="/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName"
        element={<FamilyPage />}
      />
      <Route
        path="/explore/:uid/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName"
        element={<FamilyPage />}
      />
      <Route
        path="/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName/family/:familyName"
        element={<GenusPage />}
      />
      <Route
        path="/explore/:uid/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName/family/:familyName"
        element={<GenusPage />}
      />
      <Route
        path="/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName/family/:familyName/genus/:genusName"
        element={<SpeciesPage />}
      />
      <Route
        path="/explore/:uid/kingdom/:kingdomName/phylum/:phylumName/class/:className/order/:orderName/family/:familyName/genus/:genusName"
        element={<SpeciesPage />}
      />
      <Route path="/upload" element={<UploadPage />} />
      <Route
        path="/taxonomy/reviews"
        element={<TaxonomicReviewPage uid={user.uid} />}
      />
      <Route
        path="/taxonomy/review/:revisionId"
        element={<TaxonomicReviewPage uid={user.uid} />}
      />
      <Route path="/quiz" element={<QuizSetupPage />} />
      <Route path="/quiz/play" element={<QuizPage />} />
      <Route path="/profile" element={<ProfilePageRoute />} />
      <Route path="/profile/:uid" element={<ProfilePageRoute />} />
      <Route path="/forum" element={<ForumPage />} />
      <Route path="/forum/topic/:topicId" element={<ForumTopicPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route
        path="/community-guidelines"
        element={<CommunityGuidelinesPage />}
      />
      <Route path="/copyright" element={<CopyrightPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />

      <Route
        path="/admin/taxonomy"
        element={
          <AdminRoute>
            <AdminTaxonomyPage />
          </AdminRoute>
        }
      />

      <Route
        path="/admin-audit"
        element={
          <AdminRoute>
            <AdminAuditPage />
          </AdminRoute>
        }
      />

      <Route
        path="/admin-empty-genera"
        element={
          <AdminRoute>
            <AdminEmptyGeneraPage />
          </AdminRoute>
        }
      />

      <Route
        path="/test-map"
        element={
          <AdminRoute>
            <TestMap />
          </AdminRoute>
        }
      />

      <Route
        path="/location-migration"
        element={
          <AdminRoute>
            <LocationMigrationPage />
          </AdminRoute>
        }
      />

      <Route
        path="/admin/default-taxa-images"
        element={
          <AdminRoute>
            <AdminDefaultTaxaImagesPage />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function AndroidBackButton() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener;

    CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
      } else {
        CapacitorApp.exitApp();
      }
    }).then((handle) => {
      listener = handle;
    });

    return () => {
      listener?.remove();
    };
  }, [navigate]);

  return null;
}

function App() {
  const [user, setUser] = useState(undefined);
  const [policyAcceptanceLoaded, setPolicyAcceptanceLoaded] = useState(false);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [accountSuspended, setAccountSuspended] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setPolicyAcceptanceLoaded(false);
      setHasAcceptedPolicies(false);
      setAccountStatus(null);
      setAccountSuspended(false);
      return undefined;
    }

    setPolicyAcceptanceLoaded(false);
    setHasAcceptedPolicies(false);
    setAccountStatus(null);
    setAccountSuspended(false);

    const profileRef = doc(db, "profile", user.uid);

    const unsubscribe = onSnapshot(
      profileRef,
      (profileSnapshot) => {
        const profileData = profileSnapshot.exists()
          ? profileSnapshot.data()
          : null;

        const loadedAccountStatus = profileData?.accountStatus || {
          status: "active",
        };

        setAccountStatus(loadedAccountStatus);
        setAccountSuspended(loadedAccountStatus.status === "suspended");

        setHasAcceptedPolicies(hasAcceptedCurrentPolicies(profileData));

        setPolicyAcceptanceLoaded(true);
      },
      (error) => {
        console.error("Failed to load account profile:", error);
        setAccountStatus(null);
        setAccountSuspended(false);
        setHasAcceptedPolicies(false);
        setPolicyAcceptanceLoaded(true);
      },
    );

    return () => unsubscribe();
  }, [user]);

  return (
    <Router>
      <AndroidBackButton />
      <NotificationBadgeProvider
        uid={user?.uid || ""}
        enabled={!!user && policyAcceptanceLoaded && hasAcceptedPolicies && !accountSuspended}
      >
      <AppRoutes
        user={user}
        policyAcceptanceLoaded={policyAcceptanceLoaded}
        hasAcceptedPolicies={hasAcceptedPolicies}
        accountSuspended={accountSuspended}
        accountStatus={accountStatus}
      />
      </NotificationBadgeProvider>
      {user &&
        policyAcceptanceLoaded &&
        hasAcceptedPolicies &&
        !accountSuspended && <TaxonomicNotices uid={user.uid} />}
    </Router>
  );
}

export default App;
